<template>
  <v-card variant="flat" class="grey lighten-3 my-4 rounded-lg">
    <v-card-text class="pa-0">
      <v-table class="transparent">
        <thead>
          <tr>
            <th class="table-header">
              Setor
            </th>
            <th class="table-header text-center">
              {{ $t("entities.type") }}
            </th>
            <th class="table-header text-center">
              Quantidade
            </th>
            <th class="table-header text-center">
              Valor Unitário
            </th>
            <th class="table-header text-center">
              Taxa de Serviço
            </th>
            <th class="table-header text-end">
              Subtotal
            </th>
          </tr>
        </thead>
        <tbody v-for="sector in sectors" :key="sector.id">
          <template v-for="(type, index) in filteredTypes(sector)" :key="index">
            <tr>
              <td>{{ sector.name }}</td>
              <td class="text-center">
                {{ type.name }}
              </td>
              <td class="text-center">
                {{ type.quantidade }}
              </td>
              <td class="text-center">
                {{ formatCurrency(type.current_batch?.price ?? 0) }}
              </td>
              <td class="text-center">
                {{
                  formatCurrency(
                    getTypeInfo(type).convenienceFee
                  )
                }}
              </td>
              <td class="text-end font-weight-bold">
                {{
                  formatCurrency(getTypeInfo(type).subtotal)
                }}
              </td>
            </tr>
            <tr v-for="assoc in getTypeInfo(type).coupons" :key="`coupons-${index}-${assoc.coupon.id}`">
              <td colspan="6">
                <div class="d-flex justify-space-between text-caption" style="opacity: 0.7">
                  <div class="d-flex align-center">
                    <v-btn
                      icon="mdi-close"
                      size="small"
                      color="red"
                      variant="text"
                      @click="cart.coupons.remove(assoc.coupon)"
                    />
                    <div>{{ assoc.coupon.code }}</div>
                  </div>
                  <div v-if="assoc.coupon.method === 'absolute'" style="color: green">
                    -{{ formatCurrency(assoc.discount) }}
                  </div>
                  <div v-else style="color: green">
                    - {{ formatCurrency(assoc.discount) }} ({{ assoc.coupon.value }}%)
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
        <tbody>
          <tr v-if="plan !== null && plan.interest > 0">
            <td class="td-border text-end" colspan="6">
              <span>Juros do Parcelamento em {{ plan.installments }}x: </span>
              <span>{{ formatCurrency(plan.interest / 100) }}</span>
            </td>
          </tr>
          <tr>
            <td class="td-border text-end" style="border-bottom: none !important" colspan="6">
              <span class="font-weight-bold">Total: </span>
              <span class="font-weight-bold">{{ formatCurrency(total) }}</span>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Sector from "@/types/Sector";
import Event from "@/types/Event";
import TicketType from "@/types/TicketType";
import Coupon from "@/types/Coupon";
import InstallmentPlan from "@/types/InstallmentPlan";
import formatCurrency from "@/utils/formatCurrency";
import Fee from "@/utils/fees";
import { useEventsStore } from "@/stores/events";
import { useCartStore } from "@/stores/cart";

defineOptions({
  name: "PaymentSummary",
});

interface Props {
  sectors: Sector[];
  /** The Installment plan chosen for paying by card, null until there is one. */
  plan?: InstallmentPlan | null;
}

const { plan = null } = defineProps<Props>();

const cart = useCartStore();

/** What the buyer pays: the chosen plan's amount carries its Buyer interest. */
const total = computed<number>(() => plan === null ? cart.totalAmount : plan.amount / 100);

const event = computed<Event | null>(() => useEventsStore().selected);

function filteredTypes(sector: Sector) {
  return sector.sections.flatMap(section => section.tickets).filter(t => t.quantidade > 0);
}

function getTypeInfo(type: TicketType) {
  if (event.value === null) {
    throw new Error("No event related to this payment");
  }

  const value = type.current_batch?.price ?? 0;

  const info = { ticket: type, coupons: [] as { coupon: Coupon; discount: number }[] };
  const prices = new Array<number>(type.quantidade).fill(value);

  for (const assoc of cart.coupons.applied.get(type) ?? []) {
    let discount = 0;
    for (let i = 0; i < assoc.uses; i++) {
      discount += cart.coupons.discount(prices[i], assoc.coupon);
      prices[i] -= cart.coupons.discount(prices[i], assoc.coupon);
    }
    info.coupons.push({ coupon: assoc.coupon, discount: discount });
  }

  const sum = prices.reduce((acc, price) => acc + price, 0);

  return {
    ...info,
    convenienceFee: Fee.calculate(sum, event.value.convenience_fee, event.value.convenience_fee_mode),
    subtotal: Fee.apply(sum, event.value.convenience_fee, event.value.convenience_fee_mode),
  };
}
</script>
